import React, { useMemo } from 'react';

import { Box, Flex, Heading, Text } from '@chakra-ui/react';
import ArrowIcon from 'images/icons/arrow-right.svg';
import Image from 'next/image';

import { Button } from '@/components/atoms';
import { SectionContentWrapper, SectionWrapper } from '@/components/wrappers';
import { LetsBuildBlockFragment } from '@/graphql/generated';
import { GeneralPath } from '@/utils/models';

import { DynamicTextWithDecoration } from '../DynamicTextWithDecoration';

interface LetsBuildSomethingSectionProps extends LetsBuildBlockFragment {
  overflow?: boolean;
}

function getImageUrl(attributes?: { url?: string | null } | null): string | null {
  return attributes?.url ?? null;
}

export const DynamicLetsBuildBlock = ({
  ctaText,
  subtitleBuild,
  buttonCaptionBuild,
  logosBuild = { data: [] },
}: LetsBuildSomethingSectionProps) => {
  const logoUrls = useMemo(
    () =>
      logosBuild?.data?.map((logo) => getImageUrl(logo.attributes)).filter((url): url is string => Boolean(url)) ?? [],
    [logosBuild],
  );

  return (
    <SectionWrapper
      backgroundColor="purple.500"
      zIndex={0}
      id="caseStudiesSection"
      h="auto"
      overflow="hidden"
      position="relative"
    >
      <SectionContentWrapper
        py={20}
        px={{ base: 5, md: 20 }}
        position="relative"
        h="100%"
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
      >
        <Flex flexDir="column" maxW={{ base: '100%', sm: '90%', md: '70%', xl: '60%' }} py={20} gap="60px">
          <Heading
            variant={{
              base: 'component-heading-large/xl',
              md: 'text-6xl/lineHeight-12/font-normal',
            }}
            color="white"
            textAlign="center"
          >
            <DynamicTextWithDecoration isDarkMode>{ctaText}</DynamicTextWithDecoration>
          </Heading>

          <Button
            variant="dark"
            label={buttonCaptionBuild || "Let's talk"}
            bg="white"
            rightIcon={<ArrowIcon width="12px" fill="violet.500" />}
            maxW="148px"
            url={GeneralPath.CONTACT}
            size="lg"
            alignSelf="center"
          />

          {subtitleBuild && logoUrls.length > 0 && (
            <Box>
              <Text color="purple.100" variant="text-xs/lineHeight-4/font-normal" mb={5} w="100%" textAlign="center">
                {subtitleBuild}
              </Text>
              <Flex wrap="wrap" justifyContent="space-evenly" alignItems="center" gap="80px" w="100%">
                {logoUrls.map((url, index) => (
                  <Box key={`logo-image-${index}`} h="70px" w="140px" position="relative">
                    <Image src={url} alt={`Logo ${index + 1}`} fill style={{ objectFit: 'contain' }} />
                  </Box>
                ))}
              </Flex>
            </Box>
          )}
        </Flex>
      </SectionContentWrapper>
    </SectionWrapper>
  );
};
